import { t } from "i18next";
import { useContext, useEffect, useState, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { Button, Form, Table } from "react-bootstrap";
import { getSeasonCurrent } from "../../../services/season";
import { LoadingSpinner } from "../../general/loading-spinner";
import { getTeamOptions } from "../../../services/team";
import { Team } from "../../../interfaces/team";
import { getPlanning, createPlanningWeekContent, updatePlanningWeekContent, exportWeeklyPlanningPDF } from "../../../services/planning";
import { emptyPlanning, Planning, PlanningDay, PlanningDayValue, PlanningMonth, PlanningWeek, planningLevels, PlanningWeekContent } from "../../../interfaces/planning";
import { Club } from "../../../interfaces/club";
import { getClubOptions } from "../../../services/club";
import { getAthleteTeamSeason } from "../../../services/athlete";
import { UserContext } from "../../../App";
import ReactECharts from 'echarts-for-react';
import { ContentEditModal } from "../../pages/coach/planning/ContentEditModal";

/**
 * Helper function to parse numbers with Spanish decimal format (comma)
 * Converts "8,9" to 8.9
 */
const parseSpanishFloat = (value: string): number => {
    if (!value) return 0;
    // Replace comma with dot for parseFloat
    const normalizedValue = value.replace(',', '.');
    return parseFloat(normalizedValue) || 0;
};

/**
 * PlanningView Component
 * Displays weekly training schedules in a calendar-like format
 * Features:
 * - Automatic current season loading
 * - Club and team selection
 * - Horizontal week selector showing microcycle number and date range
 * - Weekly view with days as columns and training sessions as rows
 */
function PlanningView() {
    const { userProfile } = useContext(UserContext);
    const [searchParams] = useSearchParams();

    // Check if role array contains an object with 'athlete' in the id field
    const isAthlete = userProfile.role.some((r: any) => {
        if (typeof r === 'string') return r.includes('athlete');
        if (typeof r === 'object' && r.id) return r.id.includes('athlete');
        return false;
    });

    const [loaded, setLoaded] = useState(false);
    const [loadingWeek, setLoadingWeek] = useState(false);
    const [planning, setPlanning] = useState<Planning>(emptyPlanning);
    const [selectedSeasonId, setSelectedSeasonId] = useState(0);
    const [selectedSeasonName, setSelectedSeasonName] = useState('');
    const [clubOptions, setClubOptions] = useState<Club[]>([]);
    const [selectedClubId, setSelectedClubId] = useState(0);
    const [selectedClubName, setSelectedClubName] = useState('');
    const [teamOptions, setTeamOptions] = useState<Team[]>([]);
    const [selectedTeamId, setSelectedTeamId] = useState(0);
    const [selectedTeamName, setSelectedTeamName] = useState('');
    const [selectedWeek, setSelectedWeek] = useState<PlanningWeek | null>(null);
    const [currentWeekKey, setCurrentWeekKey] = useState<string>('');
    const weekSelectorRef = useRef<HTMLDivElement>(null);
    const chartRef = useRef<any>(null);

    // Content distribution modal states
    const [showContentModal, setShowContentModal] = useState(false);
    const [selectedContentField, setSelectedContentField] = useState<string>('');
    const [selectedContentFieldName, setSelectedContentFieldName] = useState<string>('');

    // PDF export state
    const [exportingPDF, setExportingPDF] = useState(false);

    // Read URL parameters
    const urlSeasonId = searchParams.get('season_id');
    const urlTeamId = searchParams.get('team_id');
    const urlWeek = searchParams.get('week');

    /**
     * Initialize component - load current season and teams
     * Different logic for athletes vs coaches/managers
     */
    useEffect(() => {
        getSeasonCurrent().then((response: any) => {
            const currentSeason = response.data;
            if (currentSeason && currentSeason.id) {
                setSelectedSeasonId(parseInt(currentSeason.id));
                setSelectedSeasonName(currentSeason.name);

                if (isAthlete) {
                    // For athletes: Load their team-season assignments
                    const currentSeasonId = parseInt(currentSeason.id);
                    getAthleteTeamSeason(userProfile.reference_id).then((teamsResponse: any) => {
                        const teams = teamsResponse.data || [];

                        // Filter teams for current season
                        const currentSeasonTeams = teams.filter((t: any) =>
                            parseInt(t.season_id) === currentSeasonId
                        );

                        // Convert to Team format
                        const teamOptions = currentSeasonTeams.map((t: any) => ({
                            id: parseInt(t.team_id),
                            name: t.team_name,
                            club_id: parseInt(t.club_id) || 0,
                            club_name: t.club_name || ''
                        }));

                        setTeamOptions(teamOptions);

                        // If only one team, auto-select it
                        if (teamOptions.length === 1) {
                            setSelectedTeamId(teamOptions[0].id);
                            setSelectedTeamName(teamOptions[0].name);
                            // Load planning for the auto-selected team
                            setLoadingWeek(true);
                            getPlanning(currentSeasonId, teamOptions[0].id).then((response: any) => {
                                const planningData = response.data;
                                setPlanning(planningData);

                                // Find and select current week or first week
                                let weekToSelect: PlanningWeek | null = null;
                                for (const month of planningData.months) {
                                    const currentWeek = month.weeks.find((w: PlanningWeek) => w.key === currentWeekKey);
                                    if (currentWeek) {
                                        weekToSelect = currentWeek;
                                        break;
                                    }
                                }

                                // If current week not found, select first week
                                if (!weekToSelect && planningData.months.length > 0 && planningData.months[0].weeks.length > 0) {
                                    weekToSelect = planningData.months[0].weeks[0];
                                }

                                setSelectedWeek(weekToSelect);
                                setLoadingWeek(false);
                            });
                        }

                        // If athlete has teams, extract unique clubs
                        if (teamOptions.length > 0) {
                            const uniqueClubs = teamOptions.reduce((acc: Club[], team: Team) => {
                                if (!acc.find(c => c.id === team.club_id)) {
                                    acc.push({
                                        id: team.club_id,
                                        name: team.club_name || '',
                                        document_id: '',
                                        date_foundation: '',
                                        city: '',
                                        region: '',
                                        country: '',
                                        website: '',
                                        email: '',
                                        address: '',
                                        phone: '',
                                        registry_num: '',
                                        logo: [],
                                        created_by: 0,
                                        created_at: new Date(),
                                        changed_by: 0,
                                        changed_at: new Date()
                                    });
                                }
                                return acc;
                            }, []);
                            setClubOptions(uniqueClubs);

                            // If only one club, auto-select it
                            if (uniqueClubs.length === 1 && teamOptions.length === 1) {
                                setSelectedClubId(uniqueClubs[0].id);
                                setSelectedClubName(uniqueClubs[0].name);
                            }
                        }

                        setLoaded(true);
                    }).catch((error: any) => {
                        console.error('Error loading athlete teams:', error);
                        setLoaded(true);
                    });
                } else {
                    // For coaches/managers: Load clubs for current season
                    getClubOptions('', currentSeason.id).then((clubsResponse: any) => {
                        setClubOptions(clubsResponse.data);
                        setLoaded(true);
                    });
                }
            }
        });

        // Calculate current week key
        const today = new Date();
        const year = today.getFullYear();
        const week = getWeekNumber(today);
        setCurrentWeekKey(`${year}-${week.toString().padStart(2, '0')}`);
    }, [isAthlete, userProfile.reference_id]);

    /**
     * Auto-load planning when URL parameters are provided
     * This allows direct linking to a specific week
     */
    useEffect(() => {
        if (urlSeasonId && urlTeamId && urlWeek && loaded) {
            const seasonId = parseInt(urlSeasonId);
            const teamId = parseInt(urlTeamId);

            // Set the season and team IDs
            setSelectedSeasonId(seasonId);
            setSelectedTeamId(teamId);

            // Load clubs and teams to populate the selectors
            getClubOptions('', seasonId).then((clubsResponse: any) => {
                const clubs = clubsResponse.data;
                setClubOptions(clubs);

                // Load teams for the season
                getTeamOptions(seasonId, 0).then((teamsResponse: any) => {
                    const teams = teamsResponse.data;
                    setTeamOptions(teams);

                    // Find and set the selected team details
                    const selectedTeam = teams.find((t: Team) => t.id === teamId);
                    if (selectedTeam) {
                        setSelectedTeamName(selectedTeam.name);

                        // Ensure club_id is a number
                        const clubId = typeof selectedTeam.club_id === 'string'
                            ? parseInt(selectedTeam.club_id)
                            : selectedTeam.club_id;

                        setSelectedClubId(clubId);

                        // Find and set the selected club name
                        const selectedClub = clubs.find((c: Club) => c.id === clubId);
                        if (selectedClub) {
                            setSelectedClubName(selectedClub.name);
                        }
                    }

                    // Load planning for the specified parameters
                    setLoadingWeek(true);
                    getPlanning(seasonId, teamId).then((response: any) => {
                        const planningData = response.data;
                        setPlanning(planningData);

                        // Find and select the specified week
                        let weekToSelect: PlanningWeek | null = null;
                        for (const month of planningData.months) {
                            const targetWeek = month.weeks.find((w: PlanningWeek) => w.key === urlWeek);
                            if (targetWeek) {
                                weekToSelect = targetWeek;
                                break;
                            }
                        }

                        // If specified week not found, select first week
                        if (!weekToSelect && planningData.months.length > 0 && planningData.months[0].weeks.length > 0) {
                            weekToSelect = planningData.months[0].weeks[0];
                        }

                        setSelectedWeek(weekToSelect);
                        setLoadingWeek(false);

                        // Scroll to the selected week in the carousel
                        // Use a longer timeout to ensure DOM is fully rendered
                        setTimeout(() => {
                            if (weekToSelect && weekSelectorRef.current) {
                                const weekElement = weekSelectorRef.current.querySelector(`[data-week-key="${weekToSelect.key}"]`) as HTMLElement;
                                if (weekElement && weekSelectorRef.current) {
                                    // Calculate center position
                                    const containerWidth = weekSelectorRef.current.offsetWidth;
                                    const elementLeft = weekElement.offsetLeft;
                                    const elementWidth = weekElement.offsetWidth;
                                    const scrollPosition = elementLeft - (containerWidth / 2) + (elementWidth / 2);

                                    weekSelectorRef.current.scrollTo({
                                        left: scrollPosition,
                                        behavior: 'smooth'
                                    });
                                }
                            }
                        }, 300);
                    }).catch((error: any) => {
                        console.error('Error loading planning from URL parameters:', error);
                        setLoadingWeek(false);
                    });
                });
            });
        }
    }, [urlSeasonId, urlTeamId, urlWeek, loaded]);

    /**
     * Get ISO week number for a given date
     */
    const getWeekNumber = (date: Date): number => {
        const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
        const dayNum = d.getUTCDay() || 7;
        d.setUTCDate(d.getUTCDate() + 4 - dayNum);
        const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
        return Math.ceil((((d.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
    };

    /**
     * Handle club selection - load teams for selected club
     * For athletes, filter their teams by club
     */
    const handleClubChange = (event: any) => {
        const clubId = parseInt(event.target.value);
        setSelectedClubId(clubId);

        const club = clubOptions.find(c => c.id === clubId);
        setSelectedClubName(club ? club.name : '');

        if (clubId > 0 && selectedSeasonId > 0) {
            if (isAthlete) {
                // For athletes: Filter already loaded teams by selected club
                getAthleteTeamSeason(userProfile.reference_id).then((teamsResponse: any) => {
                    const teams = teamsResponse.data || [];
                    const filteredTeams = teams.filter((t: any) => {
                        const seasonMatch = parseInt(t.season_id) === selectedSeasonId;
                        const clubMatch = parseInt(t.club_id) === clubId;
                        return seasonMatch && clubMatch;
                    });

                    const teamOptions = filteredTeams.map((t: any) => ({
                        id: parseInt(t.team_id),
                        name: t.team_name,
                        club_id: parseInt(t.club_id) || 0,
                        club_name: t.club_name || ''
                    }));
                    setTeamOptions(teamOptions);

                    // If only one team, auto-select it
                    if (teamOptions.length === 1) {
                        setSelectedTeamId(teamOptions[0].id);
                        setSelectedTeamName(teamOptions[0].name);
                        // Load planning for the auto-selected team
                        setLoadingWeek(true);
                        getPlanning(selectedSeasonId, teamOptions[0].id).then((response: any) => {
                            const planningData = response.data;
                            setPlanning(planningData);

                            // Find and select current week or first week
                            let weekToSelect: PlanningWeek | null = null;
                            for (const month of planningData.months) {
                                const currentWeek = month.weeks.find((w: PlanningWeek) => w.key === currentWeekKey);
                                if (currentWeek) {
                                    weekToSelect = currentWeek;
                                    break;
                                }
                            }

                            // If current week not found, select first week
                            if (!weekToSelect && planningData.months.length > 0 && planningData.months[0].weeks.length > 0) {
                                weekToSelect = planningData.months[0].weeks[0];
                            }

                            setSelectedWeek(weekToSelect);
                            setLoadingWeek(false);
                        });
                    } else {
                        setSelectedTeamId(0);
                        setSelectedTeamName('');
                        setPlanning(emptyPlanning);
                        setSelectedWeek(null);
                    }
                });
            } else {
                // For coaches/managers: Load teams from API
                getTeamOptions(selectedSeasonId, clubId).then((response: any) => {
                    const teamOptionsData = response.data;
                    setTeamOptions(teamOptionsData);

                    // If only one team, auto-select it
                    if (teamOptionsData.length === 1) {
                        setSelectedTeamId(teamOptionsData[0].id);
                        setSelectedTeamName(teamOptionsData[0].name);
                        // Load planning for the auto-selected team
                        setLoadingWeek(true);
                        getPlanning(selectedSeasonId, teamOptionsData[0].id).then((response: any) => {
                            const planningData = response.data;
                            setPlanning(planningData);

                            // Find and select current week or first week
                            let weekToSelect: PlanningWeek | null = null;
                            for (const month of planningData.months) {
                                const currentWeek = month.weeks.find((w: PlanningWeek) => w.key === currentWeekKey);
                                if (currentWeek) {
                                    weekToSelect = currentWeek;
                                    break;
                                }
                            }

                            // If current week not found, select first week
                            if (!weekToSelect && planningData.months.length > 0 && planningData.months[0].weeks.length > 0) {
                                weekToSelect = planningData.months[0].weeks[0];
                            }

                            setSelectedWeek(weekToSelect);
                            setLoadingWeek(false);
                        });
                    } else {
                        setSelectedTeamId(0);
                        setSelectedTeamName('');
                        setPlanning(emptyPlanning);
                        setSelectedWeek(null);
                    }
                });
            }
        } else {
            setTeamOptions([]);
            setSelectedTeamId(0);
            setSelectedTeamName('');
            setPlanning(emptyPlanning);
            setSelectedWeek(null);
        }
    };

    /**
     * Handle team selection - load planning data
     */
    const handleTeamChange = (event: any) => {
        const teamId = parseInt(event.target.value);
        setSelectedTeamId(teamId);

        const team = teamOptions.find(t => t.id === teamId);
        setSelectedTeamName(team ? team.name : '');

        if (teamId > 0 && selectedSeasonId > 0) {
            setLoadingWeek(true);
            setSelectedWeek(null); // Clear selected week to force re-render
            getPlanning(selectedSeasonId, teamId).then((response: any) => {
                const planningData = response.data;
                setPlanning(planningData);

                // Find and select current week or first week
                let weekToSelect: PlanningWeek | null = null;
                for (const month of planningData.months) {
                    const currentWeek = month.weeks.find((w: PlanningWeek) => w.key === currentWeekKey);
                    if (currentWeek) {
                        weekToSelect = currentWeek;
                        break;
                    }
                }

                // If current week not found, select first week
                if (!weekToSelect && planningData.months.length > 0 && planningData.months[0].weeks.length > 0) {
                    weekToSelect = planningData.months[0].weeks[0];
                }

                setSelectedWeek(weekToSelect);
                setLoadingWeek(false);

                // Center the selected week in the carousel after render
                setTimeout(() => {
                    if (weekToSelect && weekSelectorRef.current) {
                        const weekElement = weekSelectorRef.current.querySelector(`[data-week-key="${weekToSelect.key}"]`);
                        if (weekElement && weekSelectorRef.current) {
                            const containerWidth = weekSelectorRef.current.offsetWidth;
                            const weekLeft = (weekElement as HTMLElement).offsetLeft;
                            const weekWidth = (weekElement as HTMLElement).offsetWidth;

                            // Calculate scroll position to center the week
                            const scrollPosition = weekLeft - (containerWidth / 2) + (weekWidth / 2);

                            weekSelectorRef.current.scrollTo({
                                left: scrollPosition,
                                behavior: 'smooth'
                            });
                        }
                    }
                }, 100);
            });
        } else {
            setPlanning(emptyPlanning);
            setSelectedWeek(null);
        }
    };

    /**
     * Handle week selection from horizontal list
     */
    const handleWeekClick = (week: PlanningWeek, event: React.MouseEvent<HTMLDivElement>) => {
        setSelectedWeek(week);

        // Center the selected week in the carousel
        const weekElement = event.currentTarget;
        const containerElement = weekSelectorRef.current;

        if (containerElement && weekElement) {
            const containerWidth = containerElement.offsetWidth;
            const weekLeft = weekElement.offsetLeft;
            const weekWidth = weekElement.offsetWidth;

            // Calculate scroll position to center the week
            const scrollPosition = weekLeft - (containerWidth / 2) + (weekWidth / 2);

            containerElement.scrollTo({
                left: scrollPosition,
                behavior: 'smooth'
            });
        }
    };

    /**
     * Scroll the week selector left
     */
    const scrollLeft = () => {
        if (weekSelectorRef.current) {
            weekSelectorRef.current.scrollBy({ left: -300, behavior: 'smooth' });
        }
    };

    /**
     * Scroll the week selector right
     */
    const scrollRight = () => {
        if (weekSelectorRef.current) {
            weekSelectorRef.current.scrollBy({ left: 300, behavior: 'smooth' });
        }
    };

    /**
     * Check if week has any training data
     */
    const weekHasData = (week: PlanningWeek): boolean => {
        // Check if any day has training sessions with values
        return week.days.some((day: PlanningDay) => {
            return day.values.some((value: PlanningDayValue) => {
                // Check if there's actual data in the sessions
                return value.value && value.value.trim() !== '';
            });
        });
    };

    /**
     * Export current week to PDF
     */
    const handleExportPDF = async () => {
        if (!selectedWeek) return;

        // Check if week has data
        if (!weekHasData(selectedWeek)) {
            alert(t("No training data available for this week. Cannot generate PDF."));
            return;
        }

        try {
            setExportingPDF(true);

            // Capture chart image
            let chartImage = null;
            if (chartRef.current) {
                try {
                    const echartsInstance = chartRef.current.getEchartsInstance();
                    if (echartsInstance) {
                        chartImage = echartsInstance.getDataURL({
                            type: 'png',
                            pixelRatio: 2, // Higher resolution for PDF
                            backgroundColor: '#fff'
                        });
                    }
                } catch (chartError) {
                    console.error('Error capturing chart:', chartError);
                    // Continue without chart - don't fail the whole export
                }
            }

            // Call the API to get the PDF
            const response = await exportWeeklyPlanningPDF(
                selectedSeasonId,
                selectedTeamId,
                selectedWeek.key,
                chartImage,
                selectedWeek.name
            );

            if (response.data.success) {
                // Decode base64 and create blob
                const binaryString = atob(response.data.content);
                const bytes = new Uint8Array(binaryString.length);
                for (let i = 0; i < binaryString.length; i++) {
                    bytes[i] = binaryString.charCodeAt(i);
                }
                const blob = new Blob([bytes], { type: 'application/pdf' });
                const url = URL.createObjectURL(blob);

                // Download the file
                const link = document.createElement('a');
                link.href = url;
                link.download = response.data.filename;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(url);
            } else {
                alert(t("Error generating PDF. Try again later."));
            }
        } catch (error) {
            console.error('Error exporting PDF:', error);
            alert(t("Error generating PDF. Try again later."));
        } finally {
            setExportingPDF(false);
        }
    };

    /**
     * Format date range for week display
     */
    const formatWeekRange = (week: PlanningWeek): string => {
        const fromDate = new Date(week.from);
        const toDate = new Date(week.to);

        const fromDay = fromDate.getDate();
        const fromMonth = fromDate.toLocaleDateString('es-ES', { month: 'short' });
        const fromYear = fromDate.getFullYear();
        const toDay = toDate.getDate();
        const toMonth = toDate.toLocaleDateString('es-ES', { month: 'short' });
        const toYear = toDate.getFullYear();

        // Same month and year
        if (fromMonth === toMonth && fromYear === toYear) {
            return `${fromDay}-${toDay} ${fromMonth} ${fromYear}`;
        }
        // Same year but different month
        else if (fromYear === toYear) {
            return `${fromDay} ${fromMonth} - ${toDay} ${toMonth} ${fromYear}`;
        }
        // Different year
        else {
            return `${fromDay} ${fromMonth} ${fromYear} - ${toDay} ${toMonth} ${toYear}`;
        }
    };

    /**
     * Get training sessions for a specific day and moment
     */
    const getDayMomentSessions = (day: PlanningDay, moment: string) => {
        const sessions = day.values.filter((v: PlanningDayValue) =>
            v.day_moment === moment && v.field === 'type' && v.value && v.value.trim() !== ''
        );
        return sessions;
    };

    /**
     * Get session value for a specific field
     */
    const getSessionValue = (day: PlanningDay, moment: string, session: string, field: string): string => {
        const value = day.values.find((v: PlanningDayValue) =>
            v.day_moment === moment && v.day_moment_session === session && v.field === field
        );
        return value ? value.value : '';
    };

    /**
     * Get color for a training session
     * Uses the same color coding as the mini-calendar component and backend
     * Backend rule: afternoon = #F92C84, morning = #009FE3
     */
    const getSessionColor = (day: PlanningDay, moment: string, session: string): string => {
        const colorValue = getSessionValue(day, moment, session, 'color');
        // If color is defined in the data, use it; otherwise use default based on moment
        if (colorValue) {
            return colorValue;
        }
        // Default colors matching backend logic: afternoon = pink, morning = blue
        return moment === 'afternoon' ? '#F92C84' : '#009FE3';
    };

    /**
     * Calculate daily totals for chart
     */
    const calculateDailyTotals = (week: PlanningWeek) => {
        const dailyData = week.days.map((day: PlanningDay) => {
            let totalKM = 0;
            let totalTH = 0;
            let totalGroundHours = 0;

            day.values.forEach((value: PlanningDayValue) => {
                // Calculate KM (water)
                if (value.field === 'KM' && value.value) {
                    totalKM += parseSpanishFloat(value.value);
                }

                // Calculate TH (time hours)
                if (value.field === 'TH' && value.value) {
                    const timeValue = value.value;
                    if (timeValue.includes(':')) {
                        const [hours, minutes] = timeValue.split(':').map(Number);
                        totalTH += hours + (minutes / 60);
                    } else {
                        totalTH += parseSpanishFloat(timeValue);
                    }
                }

                // Calculate ground hours (sum of all ground training levels)
                const groundLevels = planningLevels.filter(l => l.type === 'ground');
                groundLevels.forEach(level => {
                    if (value.field === level.key && value.value) {
                        totalGroundHours += parseSpanishFloat(value.value);
                    }
                });
            });

            const dayDate = new Date(day.key);
            const dayName = dayDate.toLocaleDateString('es-ES', { weekday: 'short' });
            const dayNumber = dayDate.getDate();

            return {
                label: `${dayName.charAt(0).toUpperCase() + dayName.slice(1)} ${dayNumber}`,
                km: totalKM,
                th: totalTH,
                groundHours: totalGroundHours
            };
        });

        return dailyData;
    };

    /**
     * Handle content cell click to open edit modal
     */
    const handleContentCellClick = (fieldKey: string) => {
        const field = planningLevels.find(pl => pl.key === fieldKey);
        setSelectedContentField(fieldKey);
        setSelectedContentFieldName(field?.name || fieldKey);
        setShowContentModal(true);
    };

    /**
     * Close content edit modal
     */
    const handleCloseContentModal = () => {
        setShowContentModal(false);
        setSelectedContentField('');
        setSelectedContentFieldName('');
    };

    /**
     * Save content from modal
     */
    const handleSaveContent = async (value: string, color: string, observations: string) => {
        if (!selectedWeek || !selectedWeek.id) return;

        const existingContent = selectedWeek.content?.find(c => c.field === selectedContentField);

        try {
            let newContentId = 0;

            if (existingContent && existingContent.id) {
                // Update existing content
                const body = {
                    id: existingContent.id,
                    planning_week_id: selectedWeek.id,
                    field: selectedContentField,
                    value: value,
                    color: color,
                    observations: observations
                };
                await updatePlanningWeekContent(body);
                newContentId = existingContent.id;
            } else {
                // Create new content
                const body = {
                    planning_week_id: selectedWeek.id,
                    field: selectedContentField,
                    value: value,
                    color: color,
                    observations: observations
                };
                const response = await createPlanningWeekContent(body);
                if (response.data && response.data.id) {
                    newContentId = response.data.id;
                }
            }

            // Update local state
            const updatedContent = [...(selectedWeek.content || [])];
            const contentIndex = updatedContent.findIndex(c => c.field === selectedContentField);

            if (contentIndex >= 0) {
                updatedContent[contentIndex] = {
                    ...updatedContent[contentIndex],
                    value: value,
                    color: color,
                    observations: observations
                };
            } else {
                updatedContent.push({
                    id: newContentId,
                    planning_week_id: selectedWeek.id!,
                    field: selectedContentField,
                    value: value,
                    color: color,
                    observations: observations
                });
            }

            // Update selectedWeek with new content
            const updatedWeek = { ...selectedWeek, content: updatedContent };
            setSelectedWeek(updatedWeek);

            // Also update planning state
            const updatedPlanning = {
                ...planning,
                months: planning.months.map((month: PlanningMonth) => ({
                    ...month,
                    weeks: month.weeks.map((w: PlanningWeek) => {
                        if (w.key === selectedWeek.key) {
                            return updatedWeek;
                        }
                        return w;
                    })
                }))
            };
            setPlanning(updatedPlanning);

        } catch (error) {
            console.error('Error saving content:', error);
            alert(t("Error saving content. Please try again."));
        }
    };

    /**
     * Get chart options for weekly training data
     */
    const getChartOptions = (week: PlanningWeek) => {
        const dailyData = calculateDailyTotals(week);

        return {
            title: {
                text: t('Weekly Training Distribution'),
                left: 'center',
                textStyle: {
                    fontSize: 16,
                    fontWeight: 600
                }
            },
            tooltip: {
                trigger: 'axis',
                axisPointer: {
                    type: 'cross'
                }
            },
            legend: {
                data: [t('WATER (km)'), t('TOTAL (hours)'), t('DRY (hours)')],
                top: 30
            },
            grid: {
                left: '60px',
                right: '80px',
                bottom: '3%',
                containLabel: false
            },
            xAxis: {
                type: 'category',
                boundaryGap: false,
                data: dailyData.map(d => d.label)
            },
            yAxis: [
                {
                    type: 'value',
                    name: t('Km/day'),
                    position: 'left',
                    nameTextStyle: {
                        padding: [0, 0, 0, 30]
                    }
                },
                {
                    type: 'value',
                    name: t('Hours/day'),
                    position: 'right',
                    nameTextStyle: {
                        padding: [0, 30, 0, 0]
                    }
                }
            ],
            series: [
                {
                    name: t('WATER (km)'),
                    type: 'line',
                    yAxisIndex: 0,
                    data: dailyData.map(d => d.km.toFixed(1)),
                    smooth: true,
                    lineStyle: {
                        width: 3,
                        color: '#009FE3'
                    },
                    itemStyle: {
                        color: '#009FE3'
                    }
                },
                {
                    name: t('TOTAL (hours)'),
                    type: 'line',
                    yAxisIndex: 1,
                    data: dailyData.map(d => d.th.toFixed(1)),
                    smooth: true,
                    lineStyle: {
                        width: 3,
                        color: '#34B689'
                    },
                    itemStyle: {
                        color: '#34B689'
                    }
                },
                {
                    name: t('DRY (hours)'),
                    type: 'line',
                    yAxisIndex: 1,
                    data: dailyData.map(d => d.groundHours.toFixed(1)),
                    smooth: true,
                    lineStyle: {
                        width: 3,
                        color: '#FABF48'
                    },
                    itemStyle: {
                        color: '#FABF48'
                    }
                }
            ]
        };
    };

    return (
        <div className="planning-view-container">
            <h1>{t("Weekly Planning View")}</h1>

            <div className="filters-container">
                {!loaded && <LoadingSpinner />}
                {loaded && (
                    <Form className="form-planning-filter">
                        <Form.Group className="field-club" controlId="club">
                            <Form.Select aria-label={t("Club")} value={selectedClubId} onChange={handleClubChange}>
                                <option value={0}>{t("Select a club")}</option>
                                {clubOptions.map((club: Club) => (
                                    <option key={club.id} value={club.id}>{club.name}</option>
                                ))}
                            </Form.Select>
                        </Form.Group>

                        {selectedClubId !== 0 && (
                            <Form.Group className="field-team" controlId="team">
                                <Form.Select aria-label={t("Team")} value={selectedTeamId} onChange={handleTeamChange}>
                                    <option value={0}>{t("Select a team")}</option>
                                    {teamOptions.map((team: Team) => (
                                        <option key={team.id} value={team.id}>{team.name}</option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                        )}
                    </Form>
                )}
            </div>

            {loaded && selectedTeamId > 0 && (
                <>
                    {loadingWeek && (
                        <div style={{ padding: '40px', textAlign: 'center' }}>
                            <LoadingSpinner />
                            <p>Cargando planificación...</p>
                        </div>
                    )}

                    {!loadingWeek && planning.months.length > 0 && selectedWeek && (
                    <>
                    {/* Horizontal week selector with carousel navigation */}
                    <div className="week-selector-wrapper">
                        <button className="week-nav-button left" onClick={scrollLeft} aria-label={t("Previous weeks")}>
                            <i className="bi bi-chevron-left"></i>
                        </button>
                        <div className="week-selector" ref={weekSelectorRef}>
                            {planning.months.map((month: PlanningMonth) =>
                                month.weeks.map((week: PlanningWeek) => (
                                    <div
                                        key={week.key}
                                        data-week-key={week.key}
                                        className={`week-item ${selectedWeek?.key === week.key ? 'selected' : ''} ${week.key === currentWeekKey ? 'current' : ''}`}
                                        onClick={(event) => handleWeekClick(week, event)}
                                    >
                                        <div className="week-number">{week.name}</div>
                                        <div className="week-range">{formatWeekRange(week)}</div>
                                    </div>
                                ))
                            )}
                        </div>
                        <button className="week-nav-button right" onClick={scrollRight} aria-label={t("Next weeks")}>
                            <i className="bi bi-chevron-right"></i>
                        </button>
                    </div>

                    {/* Weekly view table */}
                    {selectedWeek && (
                        <div className="weekly-view">
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <h3 className="mb-0">{t("Microcycle")} {selectedWeek.name} - {formatWeekRange(selectedWeek)}</h3>
                                <Button
                                    variant="primary"
                                    size="sm"
                                    onClick={handleExportPDF}
                                    disabled={exportingPDF}
                                >
                                    {exportingPDF ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                            {t("Generating...")}
                                        </>
                                    ) : (
                                        <>
                                            <i className="bi bi-file-pdf me-2"></i>
                                            {t("Export PDF")}
                                        </>
                                    )}
                                </Button>
                            </div>

                            <Table bordered className="weekly-view-table">
                                <thead>
                                    <tr>
                                        {selectedWeek.days.map((day: PlanningDay) => {
                                            const dayDate = new Date(day.key);
                                            const dayNumber = dayDate.getDate();
                                            const dayName = dayDate.toLocaleDateString('es-ES', { weekday: 'long' });
                                            const capitalizedDayName = dayName.charAt(0).toUpperCase() + dayName.slice(1);
                                            return (
                                                <th key={day.key} className="day-header">
                                                    {capitalizedDayName} {dayNumber}
                                                </th>
                                            );
                                        })}
                                    </tr>
                                </thead>
                                <tbody>
                                    {(() => {
                                        // Calculate the maximum number of training sessions in any day
                                        const maxSessions = Math.max(...selectedWeek.days.map((day: PlanningDay) => {
                                            const morningSessions = getDayMomentSessions(day, 'morning');
                                            const afternoonSessions = getDayMomentSessions(day, 'afternoon');
                                            return morningSessions.length + afternoonSessions.length;
                                        }));

                                        // Create a row for each training session
                                        return Array.from({ length: maxSessions }, (_, sessionIndex) => (
                                            <tr key={sessionIndex}>
                                                {selectedWeek.days.map((day: PlanningDay) => {
                                                    const morningSessions = getDayMomentSessions(day, 'morning');
                                                    const afternoonSessions = getDayMomentSessions(day, 'afternoon');
                                                    const allSessions = [...morningSessions, ...afternoonSessions];
                                                    const session = allSessions[sessionIndex];

                                                    if (!session) {
                                                        return <td key={day.key} className="day-cell empty"></td>;
                                                    }

                                                    const moment = morningSessions.includes(session) ? 'morning' : 'afternoon';
                                                    const description = getSessionValue(day, moment, session.day_moment_session, 'description');
                                                    const pct = getSessionValue(day, moment, session.day_moment_session, 'pct');
                                                    const type = session.value;
                                                    const sessionColor = getSessionColor(day, moment, session.day_moment_session);

                                                    // Build URL to session details based on user role
                                                    const sessionUrl = isAthlete
                                                        ? `#/athlete/planning/session/${selectedSeasonId}/${selectedTeamId}/${day.key}/${moment}/${session.day_moment_session}`
                                                        : `#/coach/planning/session/${selectedSeasonId}/${selectedTeamId}/${day.key}/${moment}/${session.day_moment_session}`;

                                                    return (
                                                        <td key={day.key} className="day-cell">
                                                            <div className="training-session" style={{ borderLeftColor: sessionColor }}>
                                                                <div className="session-header">
                                                                    <span className="session-type">{type}</span>
                                                                    {pct && <span className="session-pct">({pct}%)</span>}
                                                                    <a
                                                                        href={sessionUrl}
                                                                        target="_blank"
                                                                        rel="noopener noreferrer"
                                                                        className="session-details-link"
                                                                        title={t("View details")}
                                                                    >
                                                                        <i className="bi bi-box-arrow-up-right text-primary"></i>
                                                                    </a>
                                                                </div>
                                                                {description && <div className="session-description">{description}</div>}
                                                            </div>
                                                        </td>
                                                    );
                                                })}
                                            </tr>
                                        ));
                                    })()}
                                </tbody>
                            </Table>

                            {/* Summary table with training type totals */}
                            {(() => {
                                return null;
                            })()}
                            <div className="weekly-summary">
                                <h4>{t("Weekly Summary")}</h4>
                                <Table bordered className="summary-table">
                                    <thead>
                                        <tr>
                                            <th></th>
                                            {planningLevels.map((level: any) => (
                                                <th key={level.key}>{level.name}</th>
                                            ))}
                                            <th>KM</th>
                                            <th>TH</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <td className="row-label">{t("N. Sess.")}</td>
                                            {planningLevels.map((level: any) => {
                                                // Calculate total sessions for this training type
                                                let totalSessions = 0;
                                                selectedWeek.days.forEach((day: PlanningDay) => {
                                                    day.values.forEach((value: PlanningDayValue) => {
                                                        if (value.field === level.key && value.value) {
                                                            const parsedValue = parseSpanishFloat(value.value);
                                                            if (parsedValue > 0) {
                                                                totalSessions += parsedValue;
                                                            }
                                                        }
                                                    });
                                                });
                                                return (
                                                    <td key={level.key}>
                                                        {totalSessions > 0 ? Number(totalSessions.toFixed(1)).toLocaleString('es-ES', { minimumFractionDigits: 1, maximumFractionDigits: 1 }) : '-'}
                                                    </td>
                                                );
                                            })}
                                            <td>
                                                {(() => {
                                                    let totalKM = 0;
                                                    selectedWeek.days.forEach((day: PlanningDay) => {
                                                        day.values.forEach((value: PlanningDayValue) => {
                                                            if (value.field === 'KM' && value.value) {
                                                                totalKM += parseSpanishFloat(value.value);
                                                            }
                                                        });
                                                    });
                                                    return totalKM > 0 ? Number(totalKM.toFixed(1)).toLocaleString('es-ES', { minimumFractionDigits: 1, maximumFractionDigits: 1 }) : '-';
                                                })()}
                                            </td>
                                            <td>
                                                {(() => {
                                                    let totalTH = 0;
                                                    selectedWeek.days.forEach((day: PlanningDay) => {
                                                        day.values.forEach((value: PlanningDayValue) => {
                                                            if (value.field === 'TH' && value.value) {
                                                                // Parse time format (HH:MM or decimal)
                                                                const timeValue = value.value;
                                                                if (timeValue.includes(':')) {
                                                                    const [hours, minutes] = timeValue.split(':').map(Number);
                                                                    totalTH += hours + (minutes / 60);
                                                                } else {
                                                                    totalTH += parseSpanishFloat(timeValue);
                                                                }
                                                            }
                                                        });
                                                    });
                                                    if (totalTH > 0) {
                                                        const hours = Math.floor(totalTH);
                                                        const minutes = Math.round((totalTH - hours) * 60);
                                                        return `${hours}:${minutes.toString().padStart(2, '0')}`;
                                                    }
                                                    return '-';
                                                })()}
                                            </td>
                                        </tr>
                                        <tr>
                                            <td className="row-label">{t("C.D.")}</td>
                                            {planningLevels.map((level: any) => {
                                                // Get value from week.content (ContentDistribution)
                                                const contentValue = selectedWeek.content?.find(c => c.field === level.key);
                                                const value = contentValue?.value || '';
                                                const backgroundColor = contentValue?.color || '#ffffff';
                                                const hasContent = contentValue && (contentValue.value || contentValue.observations);
                                                // Determine text color based on background
                                                const isWhiteBackground = backgroundColor.toLowerCase() === '#ffffff' || backgroundColor.toLowerCase() === '#fff' || backgroundColor.toLowerCase() === 'white';
                                                const textColor = isWhiteBackground ? '#06a099' : '#fff'; // $primary color or white
                                                return (
                                                    <td
                                                        key={level.key}
                                                        className={`content-distribution-cell ${isAthlete ? 'read-only' : ''}`}
                                                        style={{
                                                            backgroundColor: backgroundColor,
                                                            color: textColor,
                                                            cursor: isAthlete ? 'default' : 'pointer',
                                                            position: 'relative'
                                                        }}
                                                        onClick={isAthlete ? undefined : () => handleContentCellClick(level.key)}
                                                    >
                                                        {value || ''}
                                                        {/* Custom tooltip showing content details */}
                                                        {hasContent && (
                                                            <div className="content-tooltip">
                                                                {contentValue.value && (
                                                                    <div className="content-tooltip-value">
                                                                        {t("Value")}: {contentValue.value}
                                                                    </div>
                                                                )}
                                                                {contentValue.observations && (
                                                                    <div className="content-tooltip-observations">
                                                                        {contentValue.observations}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        )}
                                                    </td>
                                                );
                                            })}
                                            <td>-</td>
                                            <td>-</td>
                                        </tr>
                                    </tbody>
                                </Table>
                            </div>

                            {/* Weekly training chart */}
                            <div className="weekly-chart">
                                <ReactECharts
                                    ref={chartRef}
                                    option={getChartOptions(selectedWeek)}
                                    style={{ height: '400px', width: '100%' }}
                                    opts={{ renderer: 'canvas' }}
                                />
                            </div>
                        </div>
                    )}
                    </>
                    )}
                </>
            )}

            {loaded && selectedTeamId === 0 && (
                <div className="empty-state">
                    <p>{t("Please select a club and team to view the planning")}</p>
                </div>
            )}

            {/* Content Edit Modal */}
            <ContentEditModal
                show={showContentModal}
                week={selectedWeek}
                fieldKey={selectedContentField}
                fieldName={selectedContentFieldName}
                existingContent={selectedWeek ? selectedWeek.content?.find(c => c.field === selectedContentField) || null : null}
                onClose={handleCloseContentModal}
                onSave={handleSaveContent}
            />
        </div>
    );
}

export default PlanningView;
